import type { EntscheiderWahl, InstanceGateView } from "@/types";

// #330 (UC-1/UC-2) — die EINE deutsche Schablone für „wer entscheidet an diesem Schritt".
//
// Beide Flächen der Admin-Oberfläche sprechen denselben Satz: die Einrichtungs-Seite als
// VORSCHAU („Hier entscheidet: …") und die Freigabe-Karte als IST-Anzeige („Gefragt
// ist/sind: …"). Deshalb steht die Formulierung genau einmal hier — pur, ohne React, ohne
// Datenabruf — und wird von beiden Seiten namentlich importiert.
//
// ⚠ Die LOGIK-Wahrheit ist die Server-Auflösung (#329 `resolveEntscheider`). Diese Datei
// rechnet NICHTS nach; sie formuliert nur, was ihr an Fakten gegeben wird. Wo eine Zahl
// nicht belegt ist, steht keine — nie eine erfundene (dieselbe Regel wie bei der Runde in
// `decisionLabel.ts`: `null` heisst „nicht ermittelbar", nicht „1").

/** Ist die Zahl eine brauchbare Mengen-Angabe? Sonst wird sie im Satz weggelassen. */
function zaehlbar(n: unknown): n is number {
  return typeof n === "number" && Number.isInteger(n) && n >= 0;
}

/** Rollen-Namen sind freier Text (Admin-gepflegt) — React escapt sie beim Rendern. */
function rollenName(rolle: string): string {
  return `der Rolle „${rolle}"`;
}

// --- Beschriftungen der Karte -------------------------------------------------------------
//
// ⚠ Diese Texte stehen hier und nicht im JSX, weil der Vokabular-Wächter TERNÄR-Ausdrücke im
// Prop nicht liest und ein `{KONSTANTE}` im JSX überspringt (Fix-Runde 1, MED-4). Als benannte
// Schablonen sind sie prüfbar — im JSX wären sie unbewacht (EL-16-Muster).
// (Die Objekt-Form `sekundaer: "…"` liest der Scanner seit Fix-Runde 2 mit; hier ändert das
// nichts, weil die Zuweisung dort ein Ternär ist.)

/** Markierung an einer Person, die der Fach-Rolle des Schritts angehört. */
export function rollenMitgliedLabel(rolle: string): string {
  return `in der Rolle „${rolle}"`;
}

/** Beschriftung des Umschalters — sie MUSS die Ersetzen-Semantik sagen. */
export function personenWahlLabel(hatRolle: boolean): string {
  return hatRolle
    ? "bestimmte Personen entscheiden (statt der ganzen Rolle)"
    : "bestimmte Personen entscheiden";
}

/** Warum an diesem Schritt nichts zu wählen ist. */
export function keineWahlText(hatRolle: boolean): string {
  return hatRolle
    ? "Die Vorlage lässt an diesem Schritt keine eigene Wahl zu."
    : "Dieser Schritt ist keiner Fach-Rolle zugeordnet — es entscheidet immer der Setup-Inhaber.";
}

/**
 * Der Inhaber ist an einem rollenlosen Schritt OHNE Personen-Wahl gesetzt, nicht gewählt
 * (#331: `rolle === null ∧ keine Wahl ∧ inhaberDarfEntscheiden ⇒ inhaberGewaehlt`). Das
 * Kästchen ist deshalb angehakt und gesperrt — hier steht der Grund.
 */
export const INHABER_ZWANG_TEXT =
  "Ohne Personen-Wahl entscheidet hier immer der Setup-Inhaber.";

/**
 * Ohne Adressbuch-Recht lässt sich die Auswahl nicht ERWEITERN — abwählen sehr wohl (die
 * Kennungen stehen ja schon in der Einrichtung). Der Satz sagt genau das, statt pauschal
 * „lässt sich nicht ändern": ein Satz, der mehr verbietet als die Oberfläche, ist so
 * irreführend wie einer, der zu wenig verbietet.
 */
export const KEIN_ADRESSBUCH_TEXT =
  "Sie dürfen das Firmen-Adressbuch nicht einsehen — hier lassen sich keine weiteren Personen hinzufügen. Bereits gewählte lassen sich entfernen.";

/** Ohne gelesenes Adressbuch stehen die Gewählten als Kennung da — das wird gesagt. */
export const NAMEN_UNBEKANNT_TEXT =
  "Die Namen der gewählten Personen sind gerade nicht auflösbar — hier stehen ihre Kennungen.";

/** Die Wiederholung eines gescheiterten Abrufs — beschriftet nach ihrem Zustand. */
export const WIEDERHOLEN_TEXT = "Erneut versuchen";
export const WIEDERHOLEN_LAEUFT_TEXT = "Wird geladen…";
/** Der Slot, solange noch gar nichts zu zeigen ist (auch während des Rückfall-Abrufs). */
export const PERSONEN_LADEN_TEXT = "Personen werden geladen…";

/** Chip-Marker: die Person ist intern, gehört nur nicht zur Fach-Rolle — reine Information. */
export const CHIP_NICHT_IN_ROLLE = "(nicht in der Rolle)";
/** Chip-Marker: die Person steht nicht im Adressbuch — sie kann nicht mehr entscheiden. */
export const CHIP_NICHT_VERFUEGBAR = "(nicht mehr verfügbar)";

/**
 * Was der Satz braucht. Bewusst LOSER getippt als der Wire (`anzahl` darf `null` sein): die
 * Einrichtungs-Seite rechnet die Zahl bei einer noch nicht gespeicherten Wahl selbst nach und
 * muss sagen können „gerade nicht belegbar" (Fix-Runde 1, HIGH-1). Ein `InstanceGateView`
 * bzw. `EntscheiderFakten` passt ohne Umbau hier hinein.
 */
export interface SatzFakten {
  rolle: string | null;
  inhaberGewaehlt: boolean;
  personenGewaehlt: number | null;
  anzahl: number | null;
}

/**
 * Der Satzteil hinter dem Doppelpunkt — „der Setup-Inhaber", „2 gewählte Personen",
 * „alle 5 Mitglieder der Rolle „X"".
 *
 * ⚠⚠ **Eine getroffene Wahl ERSETZT die Rolle** (#331): das Setup darf bei freigegebenem
 * Schalter beliebige interne Personen benennen, und dann wird GENAU diese Menge gefragt —
 * nicht zusätzlich die Rolle. Deshalb nennt der Satz bei einer Wahl weder die Rolle noch
 * eine Gesamtzahl: „2 gewählte Personen der Rolle ‚X' (von 5)" behauptete eine
 * Zugehörigkeit, die es nicht mehr braucht, und einen Nenner, der nichts mehr bedeutet.
 *
 * Fünf Regeln, die man beim Erweitern nicht brechen darf:
 *  1. **Ohne Rolle UND ohne Wahl entscheidet der Inhaber**, wenn er darf — sonst niemand
 *     (der Master meldet das als Unterbesetzung; der Satz sagt es geradeheraus).
 *  2. **Rolle und Wahl werden NIE addiert.** Es gilt entweder die Wahl oder die Rolle.
 *  3. **„alle N Mitglieder" nur, wenn die Rolle die EINZIGE Quelle ist.** Kommt der Inhaber
 *     dazu, zählt `anzahl` ihn mit — dann stünde dort eine Mitglieder-Zahl, die keine ist.
 *  4. **Keine Zahl ohne Beleg.** Ein fehlendes/kaputtes `anzahl` liefert „alle Mitglieder
 *     der Rolle „X"" — der Satz wird unschärfer, aber nie falsch.
 *  5. **Die AUFGELÖSTE Zahl sticht die gewählte** (Fix-Runde 1, MED-9): sind
 *     `personenGewaehlt: 2` und `anzahl: 1` beide wahr, widerspräche „Gefragt ist: 2
 *     gewählte Personen" sich selbst. Dann steht dort „1 von 2 gewählten Personen". Geprüft
 *     wird das nur OHNE Inhaber: mit ihm ist `anzahl` die Summe beider Quellen und der
 *     Wahl-Anteil nicht mehr herausrechenbar.
 *     ⚠ Am WIRE fallen die beiden seit #331 nicht mehr auseinander — nicht wegen des Sweeps,
 *     sondern weil `resolveEntscheider` bei getroffener Wahl gar keinen Abgleich mehr macht:
 *     die Menge IST die Wahl (kein Roster, gegen den geschnitten würde). Der Zweig bleibt
 *     ausschliesslich für die LOKALE Vorschau, wo gegen das eben gelesene Adressbuch
 *     geschnitten wird — dort kann eine gerade deaktivierte Person noch in der gespeicherten
 *     Wahl stehen.
 */
export function entscheiderSatz(fakten: SatzFakten): string {
  const rolle = typeof fakten?.rolle === "string" && fakten.rolle.trim() !== "" ? fakten.rolle : null;
  const inhaber = fakten?.inhaberGewaehlt === true;
  const gewaehlt = fakten?.personenGewaehlt;
  const anzahl = fakten?.anzahl;

  const teile: string[] = [];
  if (inhaber) teile.push("der Setup-Inhaber");

  if (zaehlbar(gewaehlt)) {
    // Regel 2: die Wahl ERSETZT die Rolle — kein Rollen-Zusatz, kein „(von N)".
    if (!inhaber && zaehlbar(anzahl) && anzahl !== gewaehlt) {
      teile.push(`${anzahl} von ${gewaehlt} gewählten Personen`);
    } else {
      teile.push(`${gewaehlt} ${gewaehlt === 1 ? "gewählte Person" : "gewählte Personen"}`);
    }
  } else if (rolle) {
    if (!inhaber && zaehlbar(anzahl)) {
      // Regel 3 + 4: die Rolle ist die einzige Quelle, also IST `anzahl` die Mitglieder-Zahl.
      teile.push(`alle ${anzahl} Mitglieder ${rollenName(rolle)}`);
    } else {
      teile.push(`alle Mitglieder ${rollenName(rolle)}`);
    }
  }

  // Regel 1: weder Wahl noch Rolle noch Inhaber ⇒ es gibt niemanden. Das wird GESAGT, statt
  // einen leeren Satz zu rendern — die Bereitschaft meldet es zusätzlich als Lücke.
  if (teile.length === 0) return "niemand — für diesen Schritt ist noch niemand bestimmt";

  return teile.join(" und ");
}

/**
 * „Gefragt ist" (eine Person) vs. „Gefragt sind" (mehrere) — die Freigabe-Karte benennt eine
 * Menge, die schon steht. Ohne belastbare Zahl gilt die Mehrzahl: sie ist die allgemeine
 * Form und behauptet nicht, dass genau ein Mensch gefragt sei.
 */
export function gefragtLabel(anzahl: unknown): string {
  return anzahl === 1 ? "Gefragt ist" : "Gefragt sind";
}

/**
 * Der Zusatz zum Quorum — nur wenn mehr als eine Zustimmung nötig ist. Bei Quorum 1 ist die
 * Aussage leer (jede Freigabe schliesst den Schritt ab), und ein Satz, der nichts sagt,
 * lenkt nur ab.
 */
export function quorumSatz(quorum: unknown): string | null {
  if (!zaehlbar(quorum) || quorum <= 1) return null;
  return `Nötig sind ${quorum} Zustimmungen.`;
}

/**
 * Die WIRKSAME Wahl eines Gates: was in dieser Sitzung angefasst wurde, sonst der gespeicherte
 * Stand der Einrichtung, sonst nichts.
 */
export function wirksameWahl(
  gate: InstanceGateView,
  beruehrt: Record<string, EntscheiderWahl>,
  gespeichert: Record<string, EntscheiderWahl> | undefined,
): EntscheiderWahl {
  return beruehrt[gate.stepId] ?? gespeichert?.[gate.stepId] ?? {};
}

/**
 * Sagt eine Wahl überhaupt etwas? (`personen: null` heisst seit #331 dasselbe wie „kein
 * Eintrag": keine namentliche Wahl getroffen. Der Master rechnet in beiden Fällen
 * `gewaehlt = null`.)
 */
function sagtNichts(wahl: EntscheiderWahl | undefined): boolean {
  return wahl?.inhaber === undefined && (wahl?.personen === undefined || wahl?.personen === null);
}

/** Sagen zwei Wahlen dasselbe? Reihenfolge der Personen zählt dabei nicht. */
export function gleicheWahl(a: EntscheiderWahl | undefined, b: EntscheiderWahl | undefined): boolean {
  if (a?.inhaber !== b?.inhaber) return false;
  // ⚠ `null` und `undefined` sind seit #331 DIESELBE Aussage („keine namentliche Wahl") —
  // das frühere „null = ausdrücklich alle Mitglieder" gibt es nicht mehr, seit eine Wahl die
  // Rolle ERSETZT statt sie einzuschränken. Wer beides trennt, erzeugt beim An-und-wieder-
  // Abhaken einen Patch, wo netto nichts passiert ist (L-E).
  const pa = a?.personen ?? undefined;
  const pb = b?.personen ?? undefined;
  if (!Array.isArray(pa) || !Array.isArray(pb)) return pa === pb;
  if (pa.length !== pb.length) return false;
  const sa = [...pa].sort();
  const sb = [...pb].sort();
  return sa.every((x, i) => x === sb[i]);
}

// --- Der Inhaber: EINE Ableitung, drei Konsumenten ----------------------------------------
//
// ⚠⚠ Fix-Runde 2 (HIGH-A): drei Prädikate hatten je eine eigene Auslegung derselben
// Master-Regel — und schon die zweite Abweichung liess Anzeige und Leitung auseinanderlaufen.
// Deshalb spiegeln alle drei jetzt EINE Ableitung von `resolveEntscheider` (#331):
//
// ```
// inhaberGewaehlt = inhaberDarfEntscheiden ∧ (
//     (rolle === null ∧ ¬personenWahl-getroffen)        // ZWANG: einzige Quelle
//   ∨  wahl.inhaber === true                            // WAHL
// )
// personenWahl-getroffen = personenWahlErlaubt ∧ Array.isArray(wahl.personen)
// ```

/** Hat das Setup an diesem Schritt wirksam Personen benannt? (Der Schalter muss offen sein.) */
export function personenWahlGetroffen(gate: InstanceGateView, wahl: EntscheiderWahl): boolean {
  return gate.personenWahlErlaubt === true && Array.isArray(wahl.personen);
}

/**
 * Ist der Inhaber GESETZT statt gewählt? Dann ist er die einzige Quelle des Schritts, und
 * `wahl.inhaber` sieht der Master gar nicht an — ein Kästchen dafür wäre folgenlos.
 */
export function inhaberIstGesetzt(gate: InstanceGateView, wahl: EntscheiderWahl): boolean {
  return (
    gate.rolle === null && gate.inhaberErlaubt === true && !personenWahlGetroffen(gate, wahl)
  );
}

/** Darf der Mensch den Inhaber hier an- und abwählen? (Sonst: Zwang oder gar nicht im Spiel.) */
export function inhaberIstWahl(gate: InstanceGateView, wahl: EntscheiderWahl): boolean {
  return gate.inhaberErlaubt === true && !inhaberIstGesetzt(gate, wahl);
}

/**
 * Was der Master aus DIESER Wahl machen würde — die Zahl/Anzeige-Wahrheit der Karte.
 *
 * ⚠⚠ KEIN Rückfall auf `gate.inhaberGewaehlt`: der Fakt gilt für den GESPEICHERTEN Stand.
 * Sobald Personen gewählt sind, fällt der Zwang, und ein geerbtes „true" behauptete einen
 * Inhaber, den der Patch gar nicht trägt (HIGH-A: Karte zählte 2, Master 1, und nach dem
 * nächsten Abruf kippte das Kästchen still). Den Skew-Rückfall auf den Server-Fakt macht der
 * Aufrufer — und nur dort, wo keine Personen-Wahl im Spiel ist.
 */
export function inhaberAufgeloest(gate: InstanceGateView, wahl: EntscheiderWahl): boolean {
  if (gate.inhaberErlaubt !== true) return false;
  return inhaberIstGesetzt(gate, wahl) || wahl.inhaber === true;
}

/**
 * Die zwei Schlösser gegen eine Wahl, die das Gate gar nicht erlaubt (#329 wächtert sie mit
 * einem generischen 400 — hier fällt sie schon vorher weg).
 *
 * ⚠ Es wird WEGGELASSEN, nicht auf `false`/`null` gesetzt: ein mitgeschicktes Feld ist eine
 * Aussage über einen Schalter, den die Vorlage nicht freigegeben hat.
 *
 * ⚠⚠ Angewendet wird das NUR auf eine in dieser Sitzung getroffene Wahl (Fix-Runde 1,
 * HIGH-2). Ein unberührter Bestands-Eintrag geht VERBATIM durch — auch ein unzulässig
 * gewordener. Ihn hier stillschweigend zu beschneiden hiesse: das blosse Speichern von Cron
 * oder Modell löscht eine fremde Entscheidung, ohne dass irgendwo steht, dass das passiert
 * ist. Das Räumen von Drift gehört dem Server-Sweep, der es protokolliert.
 */
export function bereinigeWahl(wahl: EntscheiderWahl, gate: InstanceGateView): EntscheiderWahl {
  const out: EntscheiderWahl = {};
  // Ist der Inhaber GESETZT (rollenlos ohne Personen-Wahl) oder gar nicht im Spiel, sieht der
  // Master `wahl.inhaber` nicht an: ein mitgeschicktes `inhaber` wäre folgenlos, die Fakten
  // kämen unverändert zurück und die Maske bliebe dauerhaft dirty (Live-Fund spike107).
  if (inhaberIstWahl(gate, wahl) && typeof wahl.inhaber === "boolean") out.inhaber = wahl.inhaber;
  // ⚠ #331: die freie Wahl braucht KEINE Rolle mehr (die Invariante „nur mit Rolle" ist
  // entfallen) — allein der Schalter entscheidet.
  if (gate.personenWahlErlaubt === true && wahl.personen !== undefined) out.personen = wahl.personen;
  return out;
}

/**
 * #330 (UC-1): was als `entscheiderOverrides` über die Leitung geht.
 *
 * ⚠⚠ Der Master ersetzt die Sammlung VOLL (wie `settingsOverrides`; `{}` = keine eigene Wahl
 * mehr). „Nur berührte Schritte senden" hiesse hier also: die Wahl aller übrigen Schritte
 * löschen. Deshalb ist der gespeicherte Stand die BASIS, und nur darüber legen sich die in
 * dieser Sitzung berührten Schritte (dort dann beschnitten auf die freigegebenen Schalter).
 *
 * Zwei Fälle, in denen der Schlüssel GANZ fehlt — beide heissen „unverändert":
 *  · in dieser Sitzung wurde nichts angefasst;
 *  · das Ergebnis sagt dasselbe wie der gespeicherte Stand (netto-null, Fix-Runde 1 LOW-12).
 */
export function baueEntscheiderPatch(
  gates: InstanceGateView[] | null | undefined,
  beruehrt: Record<string, EntscheiderWahl>,
  gespeichert: Record<string, EntscheiderWahl> | undefined,
): Record<string, EntscheiderWahl> | undefined {
  if (Object.keys(beruehrt).length === 0) return undefined;
  // Basis = alles Gespeicherte, unangetastet. Auch Einträge zu Schritten, die die aktive
  // Version nicht (mehr) kennt: sie hier wegzulassen wäre ein stilles Löschen.
  const out: Record<string, EntscheiderWahl> = { ...(gespeichert ?? {}) };
  const gateById = new Map((gates ?? []).map((g) => [g.stepId, g] as const));
  for (const [stepId, wahl] of Object.entries(beruehrt)) {
    const gate = gateById.get(stepId);
    // An einem Schritt, den die aktive Version nicht kennt, kann in dieser Maske niemand
    // etwas gewählt haben — ein solcher Eintrag wäre erfunden.
    if (!gate) continue;
    const sauber = bereinigeWahl(wahl, gate);
    if (!sagtNichts(sauber)) {
      out[stepId] = sauber;
      continue;
    }
    // Der berührte Eintrag sagt NICHTS (kein Häkchen, keine namentliche Wahl — auch
    // `{personen: null}`, seit #331 identisch zu „keine Wahl").
    //
    // ⚠⚠ Ob er deshalb WEG muss, hängt am Bestand: sagt der ebenfalls nichts (ein
    // `{personen: null}` aus der Zeit vor dieser Regel), wäre das Löschen eine ÄNDERUNG —
    // der Master prüft gelöschte Schlüssel wie neue (`pruefeQuorum(stepId, undefined)`) und
    // schriebe ein Audit für etwas, das niemand getan hat. Dann bleibt der Bestand stehen.
    if (!sagtNichts(gespeichert?.[stepId])) delete out[stepId];
  }
  return gleicheSammlung(out, gespeichert) ? undefined : out;
}

/** Sagt die neue Sammlung dasselbe wie die gespeicherte? (netto-null erzeugt keinen Patch) */
function gleicheSammlung(
  a: Record<string, EntscheiderWahl>,
  b: Record<string, EntscheiderWahl> | undefined,
): boolean {
  const ka = Object.keys(a);
  const kb = Object.keys(b ?? {});
  if (ka.length !== kb.length) return false;
  return ka.every((k) => k in (b ?? {}) && gleicheWahl(a[k], b?.[k]));
}

/**
 * Wie viele Menschen die Wahl an diesem Gate ergibt — für den Präventiv-Fehler gegen das
 * Quorum. Der Inhaber zählt nur mit, wenn er nicht ohnehin unter den gewählten Personen
 * steht (die Menge ist ein Set, dieselbe Person zählt nie doppelt).
 *
 * ⚠ `bekannt` = das gelesene Adressbuch (interne aktive Nutzer), sofern vorhanden. Nur damit
 * deckt sich die Zählung mit dem Wächter des Masters: eine gewählte Person, die nicht mehr
 * aktiv/intern ist, zählt dort nicht mit. Ohne gelesene Liste wird die eigene Auswahl roh
 * gezählt — sie ist die Eingabe des Menschen, und der Server bleibt der Richter.
 */
export function gewaehlteAnzahl(
  gate: InstanceGateView,
  wahl: EntscheiderWahl,
  ownerId: string | null,
  bekannt?: readonly string[] | null,
): number {
  const roh = Array.isArray(wahl.personen) ? wahl.personen : [];
  const personen = bekannt ? roh.filter((id) => bekannt.includes(id)) : roh;
  // ⚠ Der Inhaber kommt aus DERSELBEN Ableitung wie überall sonst — er kann auch GESETZT
  // sein (rollenlos ohne Personen-Wahl), ohne dass `wahl.inhaber` etwas dazu sagt.
  const inhaberZaehlt = inhaberAufgeloest(gate, wahl) && !!ownerId && !personen.includes(ownerId);
  return personen.length + (inhaberZaehlt ? 1 : 0);
}

/**
 * Die aufgelöste Menge einer NOCH NICHT gespeicherten PERSONEN-Wahl — die Vorschau-Zahl
 * (Fix-Runde 1, HIGH-1), nach der Ersetzen-Semantik aus #331: die Wahl IST die Menge (plus
 * Inhaber, wenn er dazugehört).
 *
 * ⚠⚠ Gerechnet wird NUR bei getroffener Personen-Wahl. Ohne sie entscheidet die Rolle — und
 * deren Grösse kennt allein der Master (Fix-Runde 1, MED-3): er zählt die
 * ZUWEISUNGS-Zeilen, das Adressbuch dagegen nur Menschen mit Nutzer-Zeile. Eine hier
 * gerechnete Rollen-Zahl wäre also systematisch zu klein. `null` heisst deshalb „das sagt
 * der Server" — der Aufrufer reicht `gate.anzahl` durch, solange die Wahl dem gespeicherten
 * Stand entspricht, und nennt sonst gar keine Zahl.
 *
 * Auch bei getroffener Wahl gilt: ohne gelesenes Adressbuch `null` — lieber unschärfer als
 * falsch.
 */
export function aufgeloesteAnzahl(
  wahl: EntscheiderWahl,
  inhaberAn: boolean,
  ownerId: string | null,
  bekannt: readonly string[] | null | undefined,
): number | null {
  if (!Array.isArray(wahl.personen)) return null;
  if (!bekannt) return null;
  const menge = new Set<string>(wahl.personen.filter((id) => bekannt.includes(id)));
  if (inhaberAn && ownerId) menge.add(ownerId);
  return menge.size;
}

/** Der Satz zur Unterbesetzung — als Fehler (eigene Eingabe) wie als Hinweis (Bestand). */
export function quorumFehlt(quorum: unknown, gewaehlt: number): string | null {
  const q = zaehlbar(quorum) ? quorum : 1;
  if (gewaehlt >= q) return null;
  return (
    `Dieser Schritt braucht ${q} ${q === 1 ? "Zustimmung" : "Zustimmungen"} — ` +
    `gewählt ${gewaehlt === 1 ? "ist" : "sind"} ${gewaehlt}.`
  );
}

/**
 * #330 (UC-1): was der Master an der EIGENEN Eingabe ablehnen würde — je Gate, VOR dem
 * Absenden (Muster `feldFehler` aus #326). Ein leeres Ergebnis heisst „absendbar", nicht
 * „richtig".
 *
 * ⚠⚠ Geprüft wird NUR, was in DIESER Sitzung gewählt wurde (Fix-Runde 1, MED-4). Eine
 * gespeicherte Unterbesetzung an einem ganz anderen Schritt darf nicht das Speichern von
 * Cron, Modell oder Reglern sperren — sie ist kein Eingabefehler dieser Maske, und die
 * Bereitschaft meldet sie ohnehin als Lücke. Sie erscheint an ihrer Zeile als Hinweis.
 *
 * Geprüft wird ausserdem nur die namentliche Personen-Wahl: steht `personen` auf „alle
 * Mitglieder", entscheidet die Besetzung der Rolle, und die wird woanders gepflegt.
 */
export function entscheiderFehler(
  gates: InstanceGateView[] | null | undefined,
  beruehrt: Record<string, EntscheiderWahl>,
  gespeichert: Record<string, EntscheiderWahl> | undefined,
  ownerId: string | null,
  mitglieder?: Record<string, readonly string[] | undefined>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const gate of gates ?? []) {
    const roh = beruehrt[gate.stepId];
    if (roh === undefined) continue;
    const wahl = bereinigeWahl(roh, gate);
    // ⚠⚠ DERSELBE Netto-Null-Filter wie im Patch-Bau (Fix-Runde 4, MED-1): sagt die Berührung
    // dasselbe wie der Bestand, geht für diesen Schritt NICHTS raus — dann darf sie auch
    // nichts sperren. Sonst entsteht eine Sackgasse für einen Patch, den es gar nicht gibt:
    // an einem rollenlosen Schritt genügte ein An-und-wieder-Abhaken, und die Einrichtung
    // liess sich für den Rest der Sitzung nicht mehr speichern — auch nicht Cron oder Modell.
    if (gleicheWahl(wahl, gespeichert?.[gate.stepId])) continue;
    // ⚠⚠ MIT Rolle heisst „keine namentliche Wahl": es entscheidet die Rolle — deren Besetzung
    // wird woanders gepflegt, ein Fehler an DIESEM Feld wäre nicht behebbar.
    // OHNE Rolle gibt es diesen Rückhalt NICHT: `personen: null` heisst dort schlicht
    // „niemand" (plus ggf. der Inhaber). Wer den Haken dort an- und wieder abschaltet, hätte
    // sonst einen leeren Schritt gespeichert — der Master lehnt ihn mit 400 ab, ohne dass die
    // Maske vorher etwas gesagt hätte.
    if (gate.rolle !== null && !Array.isArray(wahl.personen)) continue;
    const meldung = quorumFehlt(gate.quorum, gewaehlteAnzahl(gate, wahl, ownerId, mitglieder?.[gate.stepId]));
    if (meldung) out[gate.stepId] = meldung;
  }
  return out;
}
